import { SSE_ENABLED, POLLING_ENABLED, POLLING_INTERVAL } from '../config/app';

class ProgressService {
  constructor() {
    this.eventSources = new Map();
    this.subscriptions = new Map();
    this.pollingIntervals = new Map();
    this.reconnectAttempts = new Map();
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Base delay in ms
  }

  /**
   * Subscribe to progress updates for a job using SSE with fallback to polling
   * @param {string} jobId - The job ID to subscribe to
   * @param {Function} onUpdate - Callback for progress updates
   * @param {Function} onError - Callback for errors
   * @param {Function} onComplete - Callback for completion
   * @param {Object} options - Configuration options
   */
  subscribeToProgress(jobId, onUpdate, onError, onComplete, options = {}) {
    const {
      enableSSE = SSE_ENABLED, // Use global config
      fallbackToPolling = POLLING_ENABLED, // Use global config
      pollingInterval = POLLING_INTERVAL, // Use global config
      apiBaseUrl = import.meta.env.VITE_API_URL || 'https://web-api-rqa6jhyvwa-uc.a.run.app'
    } = options;

    if (this.subscriptions.has(jobId)) {
      console.log(`Already subscribed to job ${jobId}`);
      return;
    }

    // Store subscription callbacks
    this.subscriptions.set(jobId, {
      onUpdate,
      onError,
      onComplete
    });

    this.reconnectAttempts.set(jobId, 0);

    if (enableSSE) {
      this._startSSEConnection(jobId, apiBaseUrl);
    } else if (fallbackToPolling) {
      this._startPolling(jobId, apiBaseUrl, pollingInterval);
    }
  }

  /**
   * Start SSE connection for a job
   */
  _startSSEConnection(jobId, apiBaseUrl) {
    try {
      const sseUrl = `${apiBaseUrl}/api/v1/progress/${jobId}/stream`;
      const eventSource = new EventSource(sseUrl);
      
      this.eventSources.set(jobId, eventSource);

      eventSource.onopen = () => {
        console.log(`SSE connection opened for job ${jobId}`);
        this.reconnectAttempts.set(jobId, 0); // Reset reconnect attempts on success
      };

      eventSource.onmessage = (event) => {
        try {
          const progressData = JSON.parse(event.data);
          const subscription = this.subscriptions.get(jobId);
          
          if (subscription) {
            if (progressData.status === 'completed') {
              subscription.onComplete(progressData);
              this.unsubscribeFromProgress(jobId);
            } else {
              subscription.onUpdate(progressData);
            }
          }
        } catch (error) {
          console.error('Error parsing SSE data:', error);
          const subscription = this.subscriptions.get(jobId);
          if (subscription) {
            subscription.onError(new Error('Failed to parse progress data'));
          }
        }
      };

      eventSource.onerror = (error) => {
        console.error(`SSE connection error for job ${jobId}:`, error);
        eventSource.close();
        this.eventSources.delete(jobId);
        
        this._handleConnectionError(jobId, apiBaseUrl);
      };

    } catch (error) {
      console.error('Failed to create SSE connection:', error);
      this._handleConnectionError(jobId, apiBaseUrl);
    }
  }

  /**
   * Handle connection errors with exponential backoff reconnection
   */
  _handleConnectionError(jobId, apiBaseUrl) {
    const subscription = this.subscriptions.get(jobId);
    if (!subscription) return;

    const attempts = this.reconnectAttempts.get(jobId) || 0;
    
    if (attempts >= this.maxReconnectAttempts) {
      console.log(`Max reconnection attempts reached for job ${jobId}, falling back to polling`);
      this._startPolling(jobId, apiBaseUrl, 5000);
      return;
    }

    // Exponential backoff with jitter
    const delay = this.reconnectDelay * Math.pow(2, attempts) + Math.random() * 1000;
    this.reconnectAttempts.set(jobId, attempts + 1);

    subscription.onError(new Error(`Connection lost, attempting to reconnect... (${attempts + 1}/${this.maxReconnectAttempts})`));

    setTimeout(() => {
      if (this.subscriptions.has(jobId)) {
        console.log(`Attempting to reconnect SSE for job ${jobId} (attempt ${attempts + 1})`);
        this._startSSEConnection(jobId, apiBaseUrl);
      }
    }, delay);
  }

  /**
   * Start polling fallback for a job
   */
  _startPolling(jobId, apiBaseUrl, interval) {
    if (this.pollingIntervals.has(jobId)) {
      return; // Already polling
    }

    console.log(`Starting polling fallback for job ${jobId}`);

    const pollFunction = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/v1/progress/${jobId}`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const progressData = await response.json();
        const subscription = this.subscriptions.get(jobId);
        
        if (subscription) {
          if (progressData.status === 'completed') {
            subscription.onComplete(progressData);
            this.unsubscribeFromProgress(jobId);
          } else {
            subscription.onUpdate(progressData);
          }
        }
      } catch (error) {
        console.error(`Polling error for job ${jobId}:`, error);
        const subscription = this.subscriptions.get(jobId);
        if (subscription) {
          subscription.onError(error);
        }
      }
    };

    // Initial poll
    pollFunction();
    
    // Set up interval
    const intervalId = setInterval(pollFunction, interval);
    this.pollingIntervals.set(jobId, intervalId);
  }

  /**
   * Start polling manually (for direct polling without SSE)
   */
  startPolling(jobId, onUpdate, onError, onComplete, interval = POLLING_INTERVAL) {
    this.subscribeToProgress(jobId, onUpdate, onError, onComplete, {
      enableSSE: false, // Explicitly disable SSE for polling
      fallbackToPolling: POLLING_ENABLED, // Use global config
      pollingInterval: interval
    });
  }

  /**
   * Unsubscribe from progress updates for a job
   */
  unsubscribeFromProgress(jobId) {
    // Close SSE connection
    if (this.eventSources.has(jobId)) {
      const eventSource = this.eventSources.get(jobId);
      eventSource.close();
      this.eventSources.delete(jobId);
    }

    // Clear polling interval
    if (this.pollingIntervals.has(jobId)) {
      const intervalId = this.pollingIntervals.get(jobId);
      clearInterval(intervalId);
      this.pollingIntervals.delete(jobId);
    }

    // Remove subscription
    this.subscriptions.delete(jobId);
    this.reconnectAttempts.delete(jobId);

    console.log(`Unsubscribed from job ${jobId}`);
  }

  /**
   * Clean up all connections
   */
  cleanup() {
    // Close all event sources
    for (const [jobId, eventSource] of this.eventSources) {
      eventSource.close();
    }
    this.eventSources.clear();

    // Clear all polling intervals
    for (const [jobId, intervalId] of this.pollingIntervals) {
      clearInterval(intervalId);
    }
    this.pollingIntervals.clear();

    // Clear subscriptions
    this.subscriptions.clear();
    this.reconnectAttempts.clear();

    console.log('ProgressService cleaned up');
  }
}

export default ProgressService;
